import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import type { TokenPayload, SessionStatus } from '@/types/session'
import { decodeTokenLocally, validateTokenTime, readCredentialsFromUrl } from '@/utils/token'

export const useSessionStore = defineStore('session', () => {
  const rawToken = ref<string | null>(null)
  const payload = ref<TokenPayload | null>(null)
  const status = ref<SessionStatus>('none')
  const serverVerified = ref(false)

  const isTherapist = computed(() => payload.value?.role === 'therapist')
  const isPatient = computed(() => payload.value?.role === 'patient')
  const isTemplateEditor = computed(() => payload.value?.purpose === 'template-editor')
  const therapistId = computed(() => payload.value?.therapist_id ?? null)
  const channelName = computed(() => (payload.value ? `session.${payload.value.sub}` : null))
  /**
   * Handed over by the portal in the URL fragment.
   *
   * The portal is the only place a patient's name exists in plaintext - it is
   * end-to-end encrypted at rest and the portal backend has no key. So the name
   * cannot come from the token, and deliberately does not: a JWS payload is
   * readable by anyone holding it, and these tokens travel through mail
   * providers and access logs.
   */
  const patientNameFromHost = ref('')

  // `name_patient` is a legacy claim the portal no longer mints. The fallback
  // keeps tokens issued before that change working, and can be dropped once
  // none are in flight.
  const patientName = computed(() => patientNameFromHost.value || payload.value?.name_patient || '')
  const practiceName = computed(() => payload.value?.name_practice ?? '')
  /**
   * Who the patient is actually speaking to. Falls back to the practice while
   * tokens minted before the portal sent it are still in flight - the cursor
   * used to be labelled with the practice always, so a patient saw "Praxis
   * Logonetz" pointing at things rather than the person they were talking to.
   */
  const therapistName = computed(() => payload.value?.name_therapist ?? '')
  const timeStart = computed(() => payload.value?.time_start ?? 0)
  const timeEnd = computed(() => payload.value?.time_end ?? 0)

  const secondsRemaining = ref(0)

  let expiryInterval: ReturnType<typeof setInterval> | null = null

  function startExpiryTimer() {
    if (expiryInterval) clearInterval(expiryInterval)

    expiryInterval = setInterval(() => {
      if (!payload.value) return

      const now = Math.floor(Date.now() / 1000)
      // Template-editor tokens use exp instead of time_end
      const endTime = payload.value.time_end ?? payload.value.exp
      secondsRemaining.value = Math.max(0, endTime - now)

      const timeStatus = validateTokenTime(payload.value)
      if (timeStatus === 'expired') {
        status.value = 'expired'
        stopExpiryTimer()
      }
    }, 1000)
  }

  function stopExpiryTimer() {
    if (expiryInterval) {
      clearInterval(expiryInterval)
      expiryInterval = null
    }
  }

  async function initFromUrl(): Promise<void> {
    // readCredentialsFromUrl owns which channels a token can arrive on.
    const { token, patientName: hostSuppliedName } = readCredentialsFromUrl()

    if (!token) {
      status.value = 'none'
      return
    }

    if (hostSuppliedName) {
      patientNameFromHost.value = hostSuppliedName
    }

    // The token deliberately stays in the URL. It is the only thing that can
    // resume a session: nothing is persisted, so a reload with a bare URL would
    // drop the participant out of a live consultation. The router carries it
    // onto every subsequent location (see locationWithCredentials) so that any
    // page in the app survives a refresh.
    rawToken.value = token

    // Quick local decode for immediate feedback
    const localPayload = decodeTokenLocally(token)
    if (!localPayload) {
      status.value = 'invalid'
      return
    }

    // Check time locally first
    const timeStatus = validateTokenTime(localPayload)
    if (timeStatus === 'expired') {
      payload.value = localPayload
      status.value = 'expired'
      return
    }

    // Verify server-side
    status.value = 'loading'
    try {
      const response = await axios.post('/api/verify-token', null, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const serverPayload = response.data.payload as TokenPayload
      payload.value = serverPayload
      serverVerified.value = true
      status.value = validateTokenTime(serverPayload)

      if (status.value === 'valid' || status.value === 'too_early') {
        const endTime = serverPayload.time_end ?? serverPayload.exp
        secondsRemaining.value = Math.max(0, endTime - Math.floor(Date.now() / 1000))
        startExpiryTimer()
      }
    } catch (err) {
      console.error('[VSS] Token verification failed:', err)
      status.value = 'invalid'
    }
  }

  function $dispose() {
    stopExpiryTimer()
  }

  return {
    rawToken,
    payload,
    status,
    serverVerified,
    isTherapist,
    isPatient,
    isTemplateEditor,
    therapistId,
    channelName,
    patientName,
    patientNameFromHost,
    practiceName,
    therapistName,
    timeStart,
    timeEnd,
    secondsRemaining,
    initFromUrl,
    $dispose,
  }
})
